/** Kaçış core types. Pure data: no Phaser, no DOM, no Math.random. */

/** 0 up, 1 right, 2 down, 3 left. */
export type Dir = 0 | 1 | 2 | 3;
export const DX = [0, 1, 0, -1] as const;
export const DY = [-1, 0, 1, 0] as const;
export const DIRS: readonly Dir[] = [0, 1, 2, 3];
export const opposite = (d: Dir): Dir => ((d + 2) % 4) as Dir;

export type End = "head" | "tail";
export const otherEnd = (e: End): End => (e === "head" ? "tail" : "head");

export interface Cell {
  x: number;
  y: number;
}

/** A gate sits on the border: a worm whose lead end stands on (x,y) and steps `dir` (off the grid) exits. */
export interface Gate {
  x: number;
  y: number;
  dir: Dir;
  /** Index into the colour palette; only a worm of the same colour fits through. */
  color: number;
}

export interface WormDef {
  /** Index in `level.worms`; the witness solution escapes worms in id order (= target word order). */
  id: number;
  color: number;
  /** One capital letter of the level's word (Turkish or English). */
  letter: string;
  /** cells[0] = head (eyes), cells[last] = tail; contiguous orthogonal path. */
  cells: Cell[];
}

/** n = 0: permanent wall. n > 0: dissolves once n worms have escaped. */
export interface StoneDef {
  x: number;
  y: number;
  n: number;
}

/** One drag gesture: grab an end, then lead it through `steps` (the last step may be an exit). */
export interface Gesture {
  worm: number;
  end: End;
  steps: Dir[];
}

export type Band = "easy" | "medium" | "hard" | "expert" | "broken";

export interface KacisLevel {
  n: number;
  seed: number;
  cols: number;
  rows: number;
  worms: WormDef[];
  gates: Gate[];
  stones: StoneDef[];
  /** Hidden target word (3–6 letters, uppercase, in the player's language). One worm per letter. */
  word: string;
  /** Scrambled anagram of `word` shown to the player. */
  hint: string;
  /** Move budget (gestures that moved >= 1 cell). */
  budget: number;
  /** Bot's clean solution length (any order). */
  par: number;
  /** Bot's clean solution length when escaping in word order (star run). */
  starPar: number;
  band: Band;
  winRate: number;
  /** Constructive witness: escapes worms 0..n-1 in order (= the word). Replays to a win. */
  solution: Gesture[];
}
